import React from 'react';
import { Stack } from 'react-bootstrap';
import './components/positionChange/PositionChangeComponent.css';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { FormatFieldValue } from '../../../manager/MyTeam/MyTeamChangeRequests/components/FormatFieldValue';
import { RecordField, RequestFormData } from './types';
import { FieldInfo } from '../../EmployeeData/types';
import { RequestFormFragment } from '../../../__generated__/graphql';
import { Attachments } from './components/Attachments';
import { SalaryChangeData } from './components/salaryChange/types';
import { PositionChangeData } from './components/positionChange/types';
import { SalaryChangeOverview } from './components/salaryChange/SalaryChangeOverview';
import { PositionChangeOverview } from './components/positionChange/PositionChangeOverview';
import { ExtraFieldsViewAreaContent } from '../../ExtraFields/ExtraFieldsViewArea/ExtraFieldsViewAreaContent';

const convertSalaryChangeForOverview = (salaryChange: SalaryChangeData) => ({
  currentSalaryId:
    salaryChange.currentSalaryId || salaryChange.formData?.selectedSalaryId,
  newValue: salaryChange.newValue,
  effectiveDate: salaryChange.effectiveDate,
  endDate: salaryChange.endDate,
  newFrequency: salaryChange.newFrequency,
  notes: salaryChange.notes,
  isQuickUpdate: salaryChange.isQuickUpdate,
});

const convertPositionChangeForOverview = (
  positionChange: PositionChangeData,
) => {
  return {
    currentPositionId: positionChange.currentPositionId,
    newPositionId: positionChange.newPositionId,
    effectiveDate: positionChange.effectiveDate,
    endDate: positionChange.endDate,
    currentPositionName: positionChange.currentPositionName,
    newPositionName: positionChange.newPositionName,
  };
};

type StepThreeProps = {
  formData: RequestFormData;
  recordFields: Array<RecordField>;
  selectedRequestForm: RequestFormFragment | null;
  selectedEmployee?: {
    Id: string;
    Name: string;
  } | null;
  isSelectEmployeeStepVisible: boolean;
};

type RenderItemProps = {
  fieldInfo: FieldInfo;
  value: string;
  oldValue: string | null;
};

export const StepReview: React.FC<React.PropsWithChildren<StepThreeProps>> = ({
  formData,
  recordFields,
  selectedRequestForm,
  selectedEmployee,
  isSelectEmployeeStepVisible,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');

  const changeRenderItems: RenderItemProps[] = Object.entries(
    formData.fields || {},
  )
    .map(([fieldName, newValue]): RenderItemProps | null => {
      const field = recordFields.find((f) => f.fieldInfo.name === fieldName);
      if (!field) {
        return null;
      }
      return {
        fieldInfo: field.fieldInfo,
        value: newValue,
        oldValue: field.value,
      };
    })
    .filter(
      (item: RenderItemProps | null): item is RenderItemProps => item !== null,
    );

  const isChangesVisible =
    formData.positionChange ||
    formData.salaryChange ||
    changeRenderItems.length > 0;
  const isExtraFieldsVisible = !!formData.extraFields?.length;

  const recordType = selectedRequestForm?.recordTypes?.find(
    (recordType) => recordType.DeveloperName === formData.recordTypeName,
  );

  const renderItem = ({ fieldInfo, value, oldValue }: RenderItemProps) => {
    const valueProps = {
      name: fieldInfo.name,
      type: fieldInfo.type,
      options: fieldInfo.options,
    };
    return (
      <Stack key={fieldInfo.name} className="bg-light rounded p-4 mb-2">
        <div className="fw-bold mb-1">{fieldInfo.label}</div>
        <div className="text-break">
          <FormatFieldValue {...valueProps} value={value} />
        </div>
        <div className="text-muted text-decoration-line-through text-break">
          <FormatFieldValue {...valueProps} value={oldValue} />
        </div>
      </Stack>
    );
  };

  return (
    <Stack gap={3}>
      <div>
        {!selectedRequestForm?.flair__Require_Approval__c && (
          <div className="alert alert-warning">{t('autoApproval.message')}</div>
        )}
        <h5 className="mb-2">{selectedRequestForm?.Name}</h5>
        {selectedRequestForm?.flair__Information__c && (
          <div className="text-muted">
            {selectedRequestForm.flair__Information__c}
          </div>
        )}
      </div>
      {isSelectEmployeeStepVisible && selectedEmployee && (
        <div className="mb-2">
          <div className="fw-bold mb-1">{t('employee.label')}</div>
          <div>{selectedEmployee.Name}</div>
        </div>
      )}
      {formData.reason && (
        <div className="mb-2">
          <div className="fw-bold mb-1">{t('reason.label')}</div>
          <div>{formData.reason}</div>
        </div>
      )}

      {formData.files && formData.files.length > 0 && (
        <div className="mb-2">
          <div className="fw-bold mb-1">{t('attachments.label')}</div>
          <Attachments files={formData.files} readonly />
        </div>
      )}

      {isChangesVisible && (
        <div className="">
          <div className="fw-bold mb-1">{t('changes')}</div>
          <div className="step-three-fields-container px-1">
            {formData.positionChange?.newPositionId &&
              formData.positionChange.newPositionId !==
                formData.positionChange.currentPositionId && (
                <PositionChangeOverview
                  key="position-change"
                  positionChange={convertPositionChangeForOverview(
                    formData.positionChange,
                  )}
                />
              )}
            {formData.salaryChange?.newValue &&
              !formData.salaryChange?.hasPermissionIssue &&
              !formData.salaryChange?.hasNoSalary && (
                <SalaryChangeOverview
                  key="salary-change"
                  salaryChange={convertSalaryChangeForOverview(
                    formData.salaryChange,
                  )}
                  employeeId={selectedEmployee?.Id}
                />
              )}
            {changeRenderItems.map(renderItem)}
          </div>
        </div>
      )}
      {isExtraFieldsVisible && (
        <div className="">
          <div className="step-three-fields-container">
            <div className="bg-light rounded p-4 mb-2">
              <ExtraFieldsViewAreaContent
                columnsCount={1}
                formFields={selectedRequestForm?.extraFieldInfos ?? []}
                recordType={recordType ?? undefined}
                values={formData.extraFields ?? []}
              />
            </div>
          </div>
        </div>
      )}
    </Stack>
  );
};
